import { io } from "socket.io-client";

yangtze.provide("yangtze.realtime");

class RealTimeClient {
	constructor() {
		this.open_tasks = {};
		this.open_docs = new Set();
		this._pending_listeners = [];
	}

	_bind_listener(event, callback) {
		if (this.socket) {
			this.connect();
			this.socket.on(event, callback);
		} else {
			this._pending_listeners.push({ event, callback });
		}
	}

	_flush_pending_listeners() {
		if (!this.socket || !this._pending_listeners.length) {
			return;
		}
		for (const { event, callback } of this._pending_listeners) {
			this.socket.on(event, callback);
		}
		this._pending_listeners = [];
	}

	on(event, callback) {
		this._bind_listener(event, callback);
	}

	off(event, callback) {
		if (this.socket) {
			this.socket.off(event, callback);
		}
		this._pending_listeners = this._pending_listeners.filter(
			(l) => !(l.event === event && l.callback === callback)
		);
	}

	connect() {
		if (this.lazy_connect) {
			this.socket.connect();
			this.lazy_connect = false;
		}
	}

	emit(event, ...args) {
		this.connect();
		this.socket.emit(event, ...args);
	}

	init(port = 9000, lazy_connect = false) {
		if (yangtze.boot.disable_async) {
			return;
		}

		if (this.socket) {
			return;
		}
		this.lazy_connect = lazy_connect;
		let me = this;

		const opts = {
			withCredentials: true,
			reconnectionAttempts: 5,
			autoConnect: !lazy_connect,
			// Prefer polling first on HTTP/proxies that mishandle WS upgrade;
			// socket.io will still upgrade when possible.
			transports: ["polling", "websocket"],
		};
		if (window.location.protocol == "https:") {
			opts.secure = true;
		}

		if (window.location.protocol == "https:" || window.location.protocol == "http:") {
			this.socket = io(this.get_host(port), opts);
		}

		if (!this.socket) {
			console.log("Unable to connect to " + this.get_host(port));
			return;
		}

		this.socket.on("connect_error", (err) => {
			// Avoid spamming; one line is enough for diagnosis
			if (!me._connect_error_logged) {
				me._connect_error_logged = true;
				console.warn("Realtime socket connect_error:", err && err.message ? err.message : err);
			}
		});
		this.socket.on("connect", () => {
			me._connect_error_logged = false;
		});

		this.socket.on("msgprint", function (message) {
			yangtze.msgprint(message);
		});

		this.socket.on("progress", function (data) {
			if (data.progress) {
				data.percent = (flt(data.progress[0]) / data.progress[1]) * 100;
			}
			if (data.percent != null && data.percent !== "") {
				const percent = cint(data.percent);
				yangtze.show_progress(
					data.title || __("Progress"),
					percent,
					100,
					data.description || __("{0}%", [percent]),
					percent >= 100
				);
			}
		});

		this._flush_pending_listeners();
		this.setup_listeners();

		$(document).on("form-load form-rename", function (e, frm) {
			if (!frm.doc || frm.is_new()) {
				return;
			}
			me.doc_subscribe(frm.doctype, frm.docname);
		});

		$(document).on("form-refresh", function (e, frm) {
			if (!frm.doc || frm.is_new()) {
				return;
			}
			me.doc_open(frm.doctype, frm.docname);
		});

		$(document).on("form-unload", function (e, frm) {
			if (!frm.doc || frm.is_new()) {
				return;
			}

			me.doc_close(frm.doctype, frm.docname);
		});
	}

	get_host(port = 3000) {
		let host = window.location.origin;
		const socket_port = (
			(yangtze.boot && yangtze.boot.socketio_port) ||
			port ||
			"9000"
		).toString();
		const page_port = window.location.port || "";

		// bench / gunicorn on :8000 has no /socket.io proxy — connect to socketio_port directly.
		// nginx (:80/:443) proxies /socket.io on same origin — do not append :9000.
		const needs_direct_socketio =
			window.dev_server ||
			(page_port &&
				page_port !== "80" &&
				page_port !== "443" &&
				page_port !== socket_port);

		if (needs_direct_socketio) {
			const hostname = window.location.hostname;
			host = `${window.location.protocol}//${hostname}:${socket_port}`;
		}

		return host + `/${yangtze.boot.sitename}`;
	}

	subscribe(task_id, opts) {
		this.emit("task_subscribe", task_id);
		this.emit("progress_subscribe", task_id);

		this.open_tasks[task_id] = opts;
	}
	task_subscribe(task_id) {
		this.emit("task_subscribe", task_id);
	}
	task_unsubscribe(task_id) {
		this.emit("task_unsubscribe", task_id);
	}
	doctype_subscribe(doctype) {
		this.emit("doctype_subscribe", doctype);
	}
	doctype_unsubscribe(doctype) {
		this.emit("doctype_unsubscribe", doctype);
	}
	doc_subscribe(doctype, docname) {
		if (yangtze.flags.doc_subscribe) {
			console.log("throttled");
			return;
		}
		if (this.open_docs.has(`${doctype}:${docname}`)) {
			return;
		}

		yangtze.flags.doc_subscribe = true;

		// throttle to 1 per sec
		setTimeout(function () {
			yangtze.flags.doc_subscribe = false;
		}, 1000);

		this.emit("doc_subscribe", doctype, docname);
		this.open_docs.add(`${doctype}:${docname}`);
	}
	doc_unsubscribe(doctype, docname) {
		this.emit("doc_unsubscribe", doctype, docname);
		return this.open_docs.delete(`${doctype}:${docname}`);
	}
	doc_open(doctype, docname) {
		this.emit("doc_open", doctype, docname);
	}
	doc_close(doctype, docname) {
		this.emit("doc_close", doctype, docname);
	}
	setup_listeners() {
		this.socket.on("task_status_change", function (data) {
			this.process_response(data, data.status.toLowerCase());
		});
		this.socket.on("task_progress", function (data) {
			this.process_response(data, "progress");
		});
	}
	process_response(data, method) {
		if (!data) {
			return;
		}

		// success
		let opts = this.open_tasks[data.task_id];
		if (opts[method]) {
			opts[method](data);
		}

		// "callback" is std yangtze term
		if (method === "success") {
			if (opts.callback) opts.callback(data);
		}

		// always
		yangtze.request.cleanup(opts, data);
		if (opts.always) {
			opts.always(data);
		}

		// error
		if (data.status_code && data.status_code > 400 && opts.error) {
			opts.error(data);
		}
	}

	publish(event, message) {
		if (this.socket) {
			this.emit(event, message);
		}
	}
}

yangtze.realtime = new RealTimeClient();

// backward compatbility
yangtze.socketio = yangtze.realtime;
